import Logger from '../lib/logger';

const VIDEO_DESKTOP_SEL = '[data-professional-video-animation-video]';
const VIDEO_MOBILE_SEL = '[data-professional-video-animation-video-mobile]';
const POSTER_SEL = '[data-professional-video-animation-poster]';
const DOT_SEL = '[data-professional-video-animation-dot]';
const TRACK_SEL = '.professional-video-animation__timeline-track';
const LAPTOP_MQ = '(min-width: 1024px)';

const MS_PER_VIDEO_SECOND = 1000;
const SCRUB_MS_MIN = 150;
/** Max how often we assign currentTime — frequent seeks stall decoders and feel broken */
const MIN_MS_BETWEEN_SEEKS = 48;

export default class ProfessionalVideoAnimation {
	constructor(dom) {
		this.NAME = 'ProfessionalVideoAnimation';
		this.dom = dom;
		this._videoDesktop = dom.querySelector(VIDEO_DESKTOP_SEL);
		this._videoMobile = dom.querySelector(VIDEO_MOBILE_SEL);
		this.videoEl = null;
		this.posterEl = null;
		this.dotEls = dom.querySelectorAll(DOT_SEL);
		this.sequenceEls = dom.querySelectorAll(
			'.professional-video-animation__sequence[data-sequence-index]'
		);
		this._trackEl = dom.querySelector(TRACK_SEL);
		this.scrubInit = false;
		this.observer = null;
		this._scrubRafId = null;
		this._lastScrubSeekApply = 0;
		this._mql = null;
		this._primedVideos = new WeakSet();
		this._needsUserPrime = false;
		this._hasInteracted = false;
		this.onMetadataBound = this.onLoadedMetadata.bind(this);
		this.onDotClickBound = this.onDotClick.bind(this);
		this._onMqlChangeBound = this._onMqlChange.bind(this);
	}

	_resolveVideo() {
		if (this._videoMobile && !window.matchMedia(LAPTOP_MQ).matches) {
			this.videoEl = this._videoMobile;
		} else {
			this.videoEl = this._videoDesktop;
		}

		this.posterEl = this.videoEl?.parentElement?.querySelector(POSTER_SEL) || null;
	}

	_onMqlChange() {
		const prev = this.videoEl;
		const prevTime = prev ? prev.currentTime : 0;
		this._resolveVideo();
		if (this.videoEl && this.videoEl !== prev) {
			this.scrubInit = false;
			this.cancelScrubAnimation();
			this.videoEl.addEventListener('loadedmetadata', this.onMetadataBound, { once: true });
			if (Number.isFinite(this.videoEl.duration) && this.videoEl.duration > 0) {
				this.videoEl.currentTime = prevTime;
			}
			this.tryInitAfterMetadata().catch(() => {});
		}
	}

	init() {
		Logger.info(`${this.NAME} > init`);

		this._resolveVideo();

		if (this._videoDesktop && this._videoMobile) {
			this._mql = window.matchMedia(LAPTOP_MQ);
			this._mql.addEventListener('change', this._onMqlChangeBound);
		}

		if (!this.videoEl || !this.dotEls.length) return;

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.attach();
						if (this.observer) {
							this.observer.disconnect();
							this.observer = null;
						}
					}
				});
			},
			{ rootMargin: '80px' }
		);
		this.observer.observe(this.dom);
	}

	attach() {
		Logger.info(`${this.NAME} > attach`);

		this.videoEl.addEventListener('loadedmetadata', this.onMetadataBound, {
			once: true,
		});

		this.tryInitAfterMetadata().catch(() => {});

		this.dotEls.forEach((btn) => {
			btn.addEventListener('click', this.onDotClickBound);
		});
	}

	onLoadedMetadata() {
		this.tryInitAfterMetadata().catch(() => {});
	}

	async tryInitAfterMetadata() {
		const duration = this.videoEl.duration;
		if (!Number.isFinite(duration) || duration <= 0) return;
		if (this.scrubInit) return;
		this.scrubInit = true;

		this.dotEls.forEach((btn) => {
			btn.removeAttribute('disabled');
		});

		// Keep the uploaded high-resolution poster as the first state. Priming
		// the decoder here replaces it with a lower-resolution first video frame.
		// Defer that work until the user explicitly selects a timeline step.
		this._needsUserPrime = !this._primedVideos.has(this.videoEl);
		if (!this._hasInteracted) {
			this.showPoster();
		} else if (this.posterEl) {
			this.posterEl.hidden = true;
		}
		this.updateChrome(0);
	}

	async _primeVideoForScrubbing(videoEl) {
		if (!videoEl) return false;
		if (this._primedVideos.has(videoEl)) return true;

		try {
			const playResult = videoEl.play();
			if (playResult && typeof playResult.then === 'function') {
				await playResult;
			}
			await new Promise((resolve) => requestAnimationFrame(resolve));
			videoEl.pause();
			this._primedVideos.add(videoEl);
			this._needsUserPrime = false;
			return true;
		} catch (err) {
			// Autoplay rejected (e.g. Low Power Mode). Retry on the next user
			// gesture — a dot click is a valid activation event for iOS.
			Logger.info(`${this.NAME} > prime play() rejected, deferring to user gesture`, err);
			this._needsUserPrime = true;
			return false;
		}
	}

	async onDotClick(event) {
		const btn = event.currentTarget;
		const duration = this.videoEl.duration;
		if (!Number.isFinite(duration) || duration <= 0) return;

		const idx = parseInt(btn.getAttribute('data-sequence-index'), 10);
		if (Number.isNaN(idx)) return;

		if (this._needsUserPrime) {
			const primed = await this._primeVideoForScrubbing(this.videoEl);
			if (!primed) {
				// Still couldn't prime even with a user gesture. Update chrome
				// only and leave the poster in place rather than producing a
				// gray frame by setting currentTime.
				this.updateChrome(idx);
				return;
			}
		}

		this._hasInteracted = true;
		this.revealVideoWhenFrameReady();
		this.activateIndex(idx, duration, { instant: false });
	}

	showPoster() {
		if (this.posterEl) this.posterEl.hidden = false;
	}

	revealVideoWhenFrameReady() {
		if (!this.posterEl || !this.videoEl) return;

		const posterEl = this.posterEl;
		const reveal = () => {
			posterEl.hidden = true;
		};

		if (typeof this.videoEl.requestVideoFrameCallback === 'function') {
			this.videoEl.requestVideoFrameCallback(reveal);
		} else {
			this.videoEl.addEventListener('seeked', reveal, { once: true });
		}
	}

	cancelScrubAnimation() {
		if (this._scrubRafId != null) {
			cancelAnimationFrame(this._scrubRafId);
			this._scrubRafId = null;
		}
		this._lastScrubSeekApply = 0;
	}

	targetTimeForIndex(index, duration) {
		const epsilon = 0.04;
		const activeBtn = this.dom.querySelector(`${DOT_SEL}[data-sequence-index="${index}"]`);
		if (!activeBtn) return 0;

		const pct = parseFloat(activeBtn.getAttribute('data-seek-percent'));
		const safePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
		let t = (safePct / 100) * duration;
		if (safePct >= 100) {
			t = Math.max(0, duration - epsilon);
		}
		return t;
	}

	_seekPercentForIndex(index) {
		const btn = this.dom.querySelector(`${DOT_SEL}[data-sequence-index="${index}"]`);
		if (!btn) return 0;
		const pct = parseFloat(btn.getAttribute('data-seek-percent'));
		return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
	}

	_visualFractionForIndex(index) {
		const total = this.dotEls.length;
		if (total <= 1) return 0;
		return index / (total - 1);
	}

	_currentVisualFraction(currentTime, duration) {
		if (duration <= 0) return 0;
		const timePct = (currentTime / duration) * 100;
		let prevIdx = 0;
		for (let i = 0; i < this.dotEls.length; i++) {
			const sp = this._seekPercentForIndex(i);
			if (sp <= timePct) prevIdx = i;
		}
		const nextIdx = Math.min(prevIdx + 1, this.dotEls.length - 1);
		if (prevIdx === nextIdx) return this._visualFractionForIndex(prevIdx);
		const prevSeek = this._seekPercentForIndex(prevIdx);
		const nextSeek = this._seekPercentForIndex(nextIdx);
		const segRange = nextSeek - prevSeek;
		const local = segRange > 0 ? (timePct - prevSeek) / segRange : 0;
		const prevVis = this._visualFractionForIndex(prevIdx);
		const nextVis = this._visualFractionForIndex(nextIdx);
		return prevVis + (nextVis - prevVis) * local;
	}

	_updateProgress(fraction) {
		if (this._trackEl) {
			this._trackEl.style.setProperty('--pva-progress', fraction);
		}
	}

	updateChrome(index) {
		this.dotEls.forEach((b) => {
			const i = parseInt(b.getAttribute('data-sequence-index'), 10);
			const on = i === index;
			const passed = i === 0 || i <= index;
			b.classList.toggle('professional-video-animation__dot--active', on);
			b.classList.toggle('professional-video-animation__dot--passed', passed);
			b.setAttribute('aria-selected', on ? 'true' : 'false');
		});

		this.sequenceEls.forEach((panel) => {
			const i = parseInt(panel.getAttribute('data-sequence-index'), 10);
			const on = i === index;
			panel.classList.toggle('professional-video-animation__sequence--active', on);
			panel.setAttribute('aria-hidden', on ? 'false' : 'true');
		});

		this._updateProgress(this._visualFractionForIndex(index));
	}

	prefersReducedMotion() {
		if (typeof window === 'undefined' || !window.matchMedia) return false;
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	activateIndex(index, duration, options = {}) {
		const instant = options.instant === true;
		const targetTime = this.targetTimeForIndex(index, duration);
		const targetFrac = this._visualFractionForIndex(index);

		this.updateChrome(index);

		this.cancelScrubAnimation();

		if (instant || this.prefersReducedMotion()) {
			this.videoEl.currentTime = targetTime;
			this._updateProgress(targetFrac);
			return;
		}

		const startTime = this.videoEl.currentTime;
		if (Math.abs(startTime - targetTime) < 0.02) {
			this.videoEl.currentTime = targetTime;
			this._updateProgress(targetFrac);
			return;
		}

		const startFrac = this._currentVisualFraction(startTime, duration);
		const span = Math.abs(targetTime - startTime);
		const wallMs = Math.max(SCRUB_MS_MIN, span * MS_PER_VIDEO_SECOND);
		const t0 = performance.now();
		this._lastScrubSeekApply = 0;

		const tick = (now) => {
			const elapsed = now - t0;
			const p = Math.min(1, elapsed / wallMs);
			const t = startTime + (targetTime - startTime) * p;
			const currentFrac = startFrac + (targetFrac - startFrac) * p;

			const done = p >= 1;
			const due =
				done ||
				this._lastScrubSeekApply === 0 ||
				now - this._lastScrubSeekApply >= MIN_MS_BETWEEN_SEEKS;

			if (due) {
				this.videoEl.currentTime = done ? targetTime : t;
				this._lastScrubSeekApply = now;
			}

			this._updateProgress(done ? targetFrac : currentFrac);

			if (!done) {
				this._scrubRafId = requestAnimationFrame(tick);
			} else {
				this._scrubRafId = null;
				this.videoEl.currentTime = targetTime;
			}
		};

		this._scrubRafId = requestAnimationFrame(tick);
	}
}
